// ===================================================================================================
// PackagesPanel.jsx
// ---------------------------------------------------------------------------------------------------
// - Presents package inputs for FCL or LCL, based on `containerTypeId` (1=LCL, 2=FCL).
// - Pure presentational logic; state is owned by the parent and patched via the provided setters.
// - Memoized with a custom props comparator to minimize re-renders on large forms.
// ===================================================================================================

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import PackagesFCL from "./PackagesFCL";
import PackagesLCL from "./PackagesLCL";
import "./Packages.css";

// Shallow array equality (fast path for small arrays of primitives/refs)
function shallowEqualArray(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

function PackagesPanelInner({
  // Mode
  containerTypeId, // 1 = LCL, 2 = FCL

  // FCL
  fclItems,
  setFclItems,

  // LCL
  lclMode, // 'volumes' | 'dimensions'
  setLclMode,
  lclBase, // 'plt' | 'box'
  setLclBase,
  packageTypes,
  lclvolumes, // { quantity, volumeM3, weightKg }
  setLclvolumes,
  lclDimensions,
  setLclDimensions,

  // UI
  readOnly,
}) {
  const { t } = useTranslation();

  // Decide FCL/LCL once per render
  const isFcl = Number(containerTypeId) === 2;

  // Normalize LCL mode (guard on unexpected values)
  const uiMode = lclMode === "dimensions" ? "dimensions" : "volumes";
  const setUiMode = (next) =>
    setLclMode(next === "dimensions" ? "dimensions" : "volumes");

  return (
    <div className="packages-panel-root">
      {/* Title */}
      <span className="packages-panel-title">
        {isFcl ? t("FCL Packages") : t("LCL Packages")}
      </span>

      {/* Body */}
      <div className="packages-panel-body">
        {isFcl ? (
          <PackagesFCL
            items={fclItems}
            setItems={setFclItems}
            maxRows={3} // Limit rows to 3 for instant quotes
            readOnly={readOnly}
          />
        ) : (
          <PackagesLCL
            mode={uiMode}
            setMode={setUiMode}
            base={lclBase}
            setBase={setLclBase}
            packageTypes={packageTypes}
            volumes={lclvolumes}
            setvolumes={setLclvolumes}
            dimensions={lclDimensions}
            setDimensions={setLclDimensions}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}

// Custom comparator to prevent unnecessary re-renders on large forms
const areEqual = (prev, next) => {
  // Primitives / simple flags
  if (prev.containerTypeId !== next.containerTypeId) return false;
  if (prev.readOnly !== next.readOnly) return false;
  if (prev.lclMode !== next.lclMode) return false;
  if (prev.lclBase !== next.lclBase) return false;

  // Collections (ref equality)
  if (prev.packageTypes !== next.packageTypes) return false;

  // FCL/LCL specific models
  if (!shallowEqualArray(prev.fclItems || [], next.fclItems || []))
    return false;
  if (prev.lclvolumes !== next.lclvolumes) return false; // object identity matters (keep stable upstream)
  if (!shallowEqualArray(prev.lclDimensions || [], next.lclDimensions || []))
    return false;

  return true;
};

const PackagesPanel = React.memo(PackagesPanelInner, areEqual);
export default PackagesPanel;

PackagesPanel.propTypes = {
  containerTypeId: PropTypes.number,
  fclItems: PropTypes.array,
  setFclItems: PropTypes.func,
  lclMode: PropTypes.oneOf(["volumes", "dimensions"]),
  setLclMode: PropTypes.func,
  lclBase: PropTypes.oneOf(["plt", "box"]),
  setLclBase: PropTypes.func,
  packageTypes: PropTypes.array,
  lclvolumes: PropTypes.object,
  setLclvolumes: PropTypes.func,
  lclDimensions: PropTypes.array,
  setLclDimensions: PropTypes.func,
  readOnly: PropTypes.bool,
};
